import { useEffect, useState } from 'react';
import {
  getFilterOptions,
  type AppliedFilter,
  type DashboardBlock,
  type FilterOptions,
} from '../../api/dashboards';
import { Button } from '../ui';
import './FilterBar.css';

export interface FilterBarProps {
  dashboardId: string;
  /** The filter blocks of the dashboard, in the order they were placed. */
  controls: DashboardBlock[];
  value: AppliedFilter[];
  onChange: (filters: AppliedFilter[]) => void;
}

function withoutBlock(filters: AppliedFilter[], block: string): AppliedFilter[] {
  return filters.filter((filter) => filter.block !== block);
}

function currentValue(filters: AppliedFilter[], block: string): unknown {
  return filters.find((filter) => filter.block === block)?.value;
}

/**
 * The controls above a dashboard.
 *
 * A control sends what the viewer chose; it does not narrow anything itself.
 * The backend checks every filter against the block it claims to come from and
 * then against the dataset's schema, on every run — the page's idea of what is
 * filterable is never the authority (Rules 7, 8).
 */
export function FilterBar({ dashboardId, controls, value, onChange }: FilterBarProps) {
  const [options, setOptions] = useState<Record<string, FilterOptions>>({});

  useEffect(() => {
    let cancelled = false;
    // Only list-shaped controls need their values; a range asks for two dates.
    const listed = controls.filter((control) => control.display.filter_operator === 'in');

    void Promise.all(
      listed.map(async (control) => {
        try {
          return await getFilterOptions(dashboardId, control.id);
        } catch {
          return null;
        }
      }),
    ).then((loaded) => {
      if (cancelled) return;
      setOptions(
        Object.fromEntries(
          loaded
            .filter((entry): entry is FilterOptions => entry !== null)
            .map((entry) => [entry.block, entry]),
        ),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [controls, dashboardId]);

  if (!controls.length) return null;

  const set = (control: DashboardBlock, next: unknown) => {
    const field = control.display.filter_field ?? '';
    const operator = control.display.filter_operator ?? 'in';
    const cleared =
      next === null ||
      next === '' ||
      (Array.isArray(next) && (next.length === 0 || next.every((entry) => !entry)));

    onChange(
      cleared
        ? withoutBlock(value, control.id)
        : [...withoutBlock(value, control.id), { block: control.id, field, operator, value: next }],
    );
  };

  return (
    <div className="sc-filters" role="group" aria-label="Filters">
      {controls.map((control) => {
        const operator = control.display.filter_operator ?? 'in';
        const chosen = currentValue(value, control.id);
        const list = options[control.id];

        return (
          <div key={control.id} className="sc-filters__control">
            <label className="sc-filters__label" htmlFor={`filter-${control.id}`}>
              {control.title}
            </label>

            {operator === 'in' && (
              <select
                id={`filter-${control.id}`}
                value={Array.isArray(chosen) ? String(chosen[0] ?? '') : ''}
                onChange={(event) => set(control, event.target.value ? [event.target.value] : [])}
              >
                <option value="">Everything</option>
                {(list?.values ?? []).map((option) => (
                  <option key={String(option)} value={String(option ?? '')}>
                    {option === null ? '(empty)' : String(option)}
                    {list?.counts?.[String(option)] !== undefined
                      ? ` — ${list.counts[String(option)]?.toLocaleString()}`
                      : ''}
                  </option>
                ))}
              </select>
            )}

            {operator === 'between' && (
              <span className="sc-filters__range">
                <input
                  id={`filter-${control.id}`}
                  type="date"
                  aria-label={`${control.title}: from`}
                  value={Array.isArray(chosen) ? String(chosen[0] ?? '') : ''}
                  onChange={(event) =>
                    set(control, [
                      event.target.value,
                      Array.isArray(chosen) ? String(chosen[1] ?? '') : '',
                    ])
                  }
                />
                <span className="sc-filters__to" aria-hidden="true">
                  to
                </span>
                <input
                  type="date"
                  aria-label={`${control.title}: to`}
                  value={Array.isArray(chosen) ? String(chosen[1] ?? '') : ''}
                  onChange={(event) =>
                    set(control, [
                      Array.isArray(chosen) ? String(chosen[0] ?? '') : '',
                      event.target.value,
                    ])
                  }
                />
              </span>
            )}

            {operator !== 'in' && operator !== 'between' && (
              <input
                id={`filter-${control.id}`}
                type="text"
                value={
                  typeof chosen === 'string' || typeof chosen === 'number' ? String(chosen) : ''
                }
                onChange={(event) => set(control, event.target.value)}
              />
            )}

            {list?.truncated && (
              <span className="sc-filters__note">
                The first {list.values.length} values. There are more.
              </span>
            )}
          </div>
        );
      })}

      {value.length > 0 && (
        <Button variant="secondary" size="sm" onClick={() => onChange([])}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
